import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import attendeHomeBg from "assets/images/others/attendee-side-images/attendeHomeBg.png";
import { ReactComponent as NoEvents } from "assets/images/svgs/no-events.svg";
import { siteRoutes } from "utils/helpers/enums/routes";
import useStore from "hooks/useStore";
import useAttendee from "../useHooks";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import EventCard from "components/cards/event-card";
import { useSelector } from "react-redux";
import ProfileCompletionModal from "./component/ProfileCompletionModal";
import editProfileUSer from "assets/images/others/host-side-images/editProfileUSer.png";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";

const AttendeeHome: React.FC = () => {
  const navigate = useNavigate();
  const {
    isProfileCompleted,
    getUserFullName,
    getUserState,
    setProfileModalOpen,
  } = useStore();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const userFullName = getUserFullName();
  const userState = getUserState();
  const { getAttendeeHome } = useAttendee();
  const [upcomingEvents, setUpcomingEvents] = useState<EventDTO[]>([]);
  const [recommendedEvents, setRecommendedEvents] = useState<EventDTO[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const profileCompleted = isProfileCompleted();
  const profileModalOpen = useSelector(
    (state: any) => state.sharedReducer.profileModalOpen
  );

  useEffect(() => {
    if (!profileCompleted) {
      setProfileModalOpen(true);
      const interval = setInterval(() => {
        setProfileModalOpen(true);
      }, 5 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [profileCompleted]);

  useEffect(() => {
    if (profileCompleted)
      getAttendeeHome(setUpcomingEvents, setRecommendedEvents);
  }, []);

  // Mark initial load as complete after first data load
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, upcomingEvents, recommendedEvents]);

  // Sort events by start date (nearest first)
  const sortEventsByDate = (events: EventDTO[]) => {
    return [...events].sort((a, b) => {
      const getEarliestDate = (event: EventDTO) => {
        if (event.workshopSessions && event.workshopSessions.length > 0) {
          // Get the earliest workshop session date
          const dates = event.workshopSessions
            .map((session: any) => {
              if (session.startDateTime) {
                const date = new Date(session.startDateTime);
                return isNaN(date.getTime()) ? null : date;
              }
              return null;
            })
            .filter((date) => date !== null);

          if (dates.length > 0) {
            return Math.min(...dates.map((date) => date!.getTime()));
          }
        }
        // Fallback to event creation date or current date
        return new Date().getTime();
      };

      const aDate = getEarliestDate(a);
      const bDate = getEarliestDate(b);

      return aDate - bDate;
    });
  };

  // Get sorted events
  const sortedUpcomingEvents = sortEventsByDate(upcomingEvents);
  const sortedRecommendedEvents = sortEventsByDate(recommendedEvents);

  // Navigation handlers for "See More" buttons
  const handleUpcommingSeeMore = () => {
    navigate(`${siteRoutes.attendeeExperiences}?tab=upcoming`);
  };
  const handleRecommendedSeeMore = () => {
    navigate(`${siteRoutes.attendeeExperiences}?tab=recommended`);
  };

  const goToExpListing = () => {
    navigate(siteRoutes.attendeeExperiences);
  };

  return (
    <div>
      <div
        style={{ backgroundImage: `url(${attendeHomeBg})` }}
        className="bg-cover bg-center bg-no-repeat h-full md:h-[300px] w-full"
      >
        <div className="host-bg-gradient h-full ">
          <div className="container">
            <div className="flex items-center my-5 md:mt-16">
              <div className="flex justify-center flex-col gap-2">
                <p className="text-2xl text-primaryBlueText">Welcome</p>
                <h2 className="text-4xl text-primaryBlueText capitalize">
                  {userFullName}!
                </h2>
                <p className="text-[21px] text-primaryBlueText font-medium mt-3">
                  Build skills and confidence in a fun, hands-on way.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* above code is for the home page */}
      <div className={`container ${profileCompleted ? "min-h-[50vh]" : ""}`}>
        {profileCompleted ? (
          <div className="mt-5 md:mt-10">
            {/* Show Upcoming Experiences in first row if available */}
            {sortedUpcomingEvents?.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-4">
                  Upcoming Experiences
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
                  {sortedUpcomingEvents.map((event, index) => (
                    <EventCard event={event} key={index} showBookmark={false} />
                  ))}
                </div>

                {sortedUpcomingEvents?.length >= 12 && (
                  <div className="text-center mt-10">
                    <button
                      onClick={handleUpcommingSeeMore}
                      className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
                    >
                      See More
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Show Recommended Experiences */}
            {sortedRecommendedEvents?.length > 0 && (
              <div
                className={
                  sortedUpcomingEvents?.length > 0 ? "my-5 md:my-10" : ""
                }
              >
                <h2 className="text-2xl font-bold mb-4">
                  Recommended Experiences
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
                  {sortedRecommendedEvents.map((event, index) => (
                    <EventCard event={event} key={index} showBookmark={false} />
                  ))}
                </div>

                {sortedRecommendedEvents?.length >= 12 && (
                  <div className="text-center mt-10">
                    <button
                      onClick={handleRecommendedSeeMore}
                      className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
                    >
                      See More
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center gap-3 min-h-[50vh]">
            <span>
              <NoEvents />
            </span>
            <p>No Events to Show</p>
            <button
              onClick={() => navigate(siteRoutes.parentGuardianDetails)}
              className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
            >
              Complete Profile
            </button>
          </div>
        )}
        <div className="flex items-center justify-center h-full min-h-[50vh]">
          <CustomMessageDisplay
            show={
              profileCompleted &&
              !isLoading &&
              !isInitialLoad &&
              !sortedUpcomingEvents.length &&
              !sortedRecommendedEvents.length
            }
            title="No Experiences Available"
            slogan="No experiences available at this moment."
            image={noFoundGif}
            imageAlt="No events found"
            className="py-16"
            imageClassName="w-24 h-24"
            titleClassName="text-2xl font-bold text-blackie"
            sloganClassName="text-gray-500 text-base"
          />
        </div>
      </div>

      {profileModalOpen && <ProfileCompletionModal />}
    </div>
  );
};

export default AttendeeHome;
